import {
  type AutomationTemplate,
  type AutomationTemplateId,
  AutomationTemplateIds,
} from "@repo/app-types/entities/automation";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { type FC, useState } from "react";
import { Dialog, GridList, GridListItem } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { automationTemplateIcons } from "./automationTemplateIcons.js";

export interface AutomationTemplateChooserDialogProps {
  templates: readonly AutomationTemplate[];
  isCreating?: boolean;
  onChoose: (templateId: AutomationTemplateId) => void;
}

/**
 * "Choose a template" step of the create-agent flow. Selecting a template and
 * confirming hands off to `onChoose`, which immediately creates the agent
 * (disabled, seeded from the current playbook) and opens it.
 *
 * Render this inside a react-aria `<DialogTrigger>` alongside the trigger
 * button — the trigger owns the open state. The selection lives in
 * {@link AutomationTemplateChooserDialogContent}, which the modal mounts only
 * while open, so the choice resets to the default on every reopen.
 */
export const AutomationTemplateChooserDialog: FC<
  AutomationTemplateChooserDialogProps
> = (props) => {
  const { templates, isCreating = false, onChoose } = props;
  return (
    <CenteredModal isDismissable size={Size.LARGE}>
      <Dialog className="w-full max-w-2xl px-4 pt-5 pb-4 text-left outline-hidden sm:p-6">
        {({ close }) => (
          <AutomationTemplateChooserDialogContent
            templates={templates}
            isCreating={isCreating}
            onClose={close}
            onChoose={onChoose}
          />
        )}
      </Dialog>
    </CenteredModal>
  );
};

interface AutomationTemplateChooserDialogContentProps {
  templates: readonly AutomationTemplate[];
  isCreating: boolean;
  onClose: () => void;
  onChoose: (templateId: AutomationTemplateId) => void;
}

const AutomationTemplateChooserDialogContent: FC<
  AutomationTemplateChooserDialogContentProps
> = (props) => {
  const { templates, isCreating, onClose, onChoose } = props;
  const [templateId, setTemplateId] = useState<AutomationTemplateId>(
    templates[0]?.id ?? AutomationTemplateIds.TEAMMATE_COACHING,
  );

  return (
    <>
      <ModalHeader onClose={onClose}>Choose a template</ModalHeader>
      <p className="mt-2 text-sm text-muted-foreground">
        Choose a starting point. You can change when it runs, which calls it
        reviews, and who receives the results.
      </p>
      <GridList
        aria-label="Agent template"
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={[templateId]}
        onSelectionChange={(keys) => {
          if (keys === "all") {
            return;
          }
          const [selectedKey] = keys;
          const selected = templates.find(
            (template) => template.id === selectedKey,
          );
          if (selected) {
            setTemplateId(selected.id);
          }
        }}
        // Negative margin + matching padding gives the selected card's ring
        // room inside the scroll container, which would otherwise clip it.
        className="-mx-1 mt-4 flex max-h-88 flex-col gap-3 overflow-y-auto px-1 py-1"
      >
        {templates.map((template) => {
          const Icon = automationTemplateIcons[template.id];
          return (
            <GridListItem
              key={template.id}
              id={template.id}
              textValue={template.name}
              className={twMerge(
                "group flex cursor-pointer items-start gap-3 rounded-lg border border-border-light bg-white p-4 outline-hidden transition-colors hover:bg-gray-50",
                "selected:border-indigo-600 selected:ring-1 selected:ring-indigo-600",
                "focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2",
              )}
            >
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600">
                <Icon aria-hidden className="size-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-foreground">
                  {template.name}
                </span>
                <span className="mt-0.5 line-clamp-2 text-sm leading-5 text-muted-foreground">
                  {template.description}
                </span>
              </span>
              <span
                aria-hidden
                className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-gray-300 bg-white transition-colors group-selected:border-indigo-600 group-selected:bg-indigo-600"
              >
                <span className="size-2 rounded-full bg-white opacity-0 group-selected:opacity-100" />
              </span>
            </GridListItem>
          );
        })}
      </GridList>
      <div className="mt-4 flex justify-end space-x-2 border-t border-border-light pt-4">
        <Button
          variant={ButtonVariant.Secondary}
          isDisabled={isCreating}
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          isLoading={isCreating}
          onClick={() => {
            onChoose(templateId);
          }}
        >
          Create agent
        </Button>
      </div>
    </>
  );
};
